
import React, { useState, useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import { supabase } from '@/integrations/supabase/client';
import { fromTable } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { 
  FormHeader, 
  RequesterMessage,
  QuestionFields,
  AnonymityOption,
  SubmitButton,
  FormStates,
  LoadingState,
  ErrorState
} from './components';
import { useToast } from '@/hooks/use-toast';
import { useFeedbackRequest } from './hooks/useFeedbackRequest';
import { useQuestionsTemplate } from './hooks/useQuestionsTemplate';
import { Card, CardContent } from '@/components/ui/card';

// Helper function to determine direction based on request data
const determineDirection = (request: any): string => {
  if (request.direction) return request.direction;
  return request.recruiter_email ? 'candidate_to_recruiter' : 'recruiter_to_candidate';
};

interface FeedbackResponseFormProps {
  requestId: string;
}

const FeedbackResponseForm: React.FC<FeedbackResponseFormProps> = ({ requestId }) => {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { user } = useAuth();
  const { toast } = useToast();
  const resolvedRequestId = requestId;
  
  const { request, loading: requestLoading, error: requestError } = useFeedbackRequest(resolvedRequestId);
  const { questions, schema, loadQuestionTemplate, generateDefaultValues, loading: questionsLoading } = useQuestionsTemplate();
  
  // Create form with react-hook-form
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: generateDefaultValues(questions),
    mode: 'onChange'
  });
  
  // Reset form when questions change
  useEffect(() => {
    if (questions.length > 0) {
      form.reset(generateDefaultValues(questions));
    }
  }, [questions, form, generateDefaultValues]);
  
  // Load questions when request context is available
  useEffect(() => {
    if (request?.context) {
      const direction = determineDirection(request);
      loadQuestionTemplate(request.context, direction);
    }
  }, [request, loadQuestionTemplate]);
  
  // Vérifier si l'utilisateur a le droit de répondre à cette demande
  const canRespond = useMemo(() => {
    if (!user || !request) return false;
    
    // Vérifier si l'utilisateur est le destinataire de la demande
    const isRecipient = 
      // Pour les demandes recruteur vers candidat
      (request.direction === 'recruiter_to_candidate' && 
       (request.candidate_email === user.email || request.candidate_user_id === user.id)) ||
      // Pour les demandes candidat vers recruteur  
      (request.direction === 'candidate_to_recruiter' && 
       (request.recruiter_email === user.email || 
        (request.recruiter_user_id !== undefined && request.recruiter_user_id === user.id)));
    
    return isRecipient && request.status === 'pending';
  }, [user, request]);
  
  const onSubmit = async (data: any) => {
    if (!user || !request) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour soumettre un feedback",
        variant: "destructive",
      });
      return;
    }
    
    // Vérifier à nouveau si l'utilisateur peut répondre
    if (!canRespond) {
      toast({
        title: "Erreur",
        description: "Vous n'êtes pas autorisé à répondre à cette demande",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setFormState('submitting');
      
      // Déterminer la table source basée sur la direction
      const sourceTables = request.direction?.includes('candidate_to_recruiter')
        ? 'candidate_to_recruiter_feedback_requests'
        : 'feedback_requests';
      
      // Create the feedback response using fromTable helper for tables not yet in the type definition
      const { data: responseData, error: responseError } = await fromTable('feedback_responses')
        .insert({
          request_id: request.id,
          answers: data,
          is_anonymous: isAnonymous,
          user_id: user.id,
          submitted_at: new Date().toISOString()
        })
        .select('id')
        .single();
      
      if (responseError) throw responseError;
      
      // Update the request status to 'completed'
      const tableName = request.direction?.includes('candidate_to_recruiter') 
        ? 'candidate_to_recruiter_feedback_requests' 
        : 'feedback_requests';
      
      const { error: updateError } = await supabase
        .from(tableName)
        .update({ status: 'completed' })
        .eq('id', request.id);
      
      if (updateError) throw updateError;
      
      // If feedback_id is present, update the feedback record
      if (request.feedback_id) {
        const { error: feedbackError } = await supabase
          .from('feedbacks')
          .update({
            status: 'completed'
          })
          .eq('id', request.feedback_id);
        
        if (feedbackError) throw feedbackError;
      }
      
      setFormState('success');
      
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      setFormState('error');
      setErrorMessage(error.message || "Une erreur est survenue lors de l'envoi du feedback.");
    }
  };
  
  if (requestLoading || questionsLoading) {
    return <LoadingState />;
  }
  
  if (requestError) {
    return <ErrorState error={requestError} />;
  }
  
  if (!request) {
    return <ErrorState error="Demande de feedback introuvable" />;
  }
  
  // Afficher un message d'erreur spécifique si l'utilisateur n'a pas le droit de répondre
  if (!canRespond && formState === 'idle') {
    return <ErrorState error="Vous n'êtes pas autorisé à répondre à cette demande de feedback" />;
  }
  
  if (formState === 'success' || formState === 'error') {
    return <FormStates state={formState} customMessage={errorMessage} />;
  }

  return (
    <Card className="max-w-xl mx-auto">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormHeader request={request} />
          
          <CardContent className="space-y-4">
            {request.message && (
              <RequesterMessage message={request.message} />
            )}
            
            <QuestionFields 
              questions={questions} 
              form={form} 
            />
            
            <AnonymityOption 
              form={form}
              isAnonymous={isAnonymous} 
              setIsAnonymous={setIsAnonymous} 
            />
          </CardContent>
          
          <SubmitButton 
            isSubmitting={formState === 'submitting'} 
            onSubmit={form.handleSubmit(onSubmit)}
          />
        </form>
      </FormProvider>
    </Card>
  );
};

export default FeedbackResponseForm;
